import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { FilterManager, IBaseFilters } from '../../../../common/filter/FilterManager'
import { getMerchantLookupWithToken } from '../../../../common/core/_requests'
import { convertToUTCByMSK } from '../../../../../utils'

import OverpaymentsTable from './components/OverpaymentsTable/OverpaymentsTable'
import LocalAdapterModal from './components/LocalAdapterModal/LocalAdapterModal'
import DateRangeFilter from './components/DateRangeFilter/DateRangeFilter'
import ExportButton from './components/ExportButton/ExportButton'
import { getOverpaymentList, getOverpaymentReasonLookup } from './core/api'
import { OverpaymentRecord, OverpaymentReasonLookupItem } from './core/response.types'
import { OverpaymentFilters, UiStatus } from './core/types'
import { OVERPAYMENTS_BREADCRUMBS, UI_STATUS_LABEL } from './core/consts'
import { OverpaymentQuery } from './core/query.types'
import { IsGranted, useAuth } from '../../../auth'
import { matchingCapabilities } from '../../core/_models'

const PAGE_SIZE = 50

const SEARCH_FIELDS = {
  RECIPIENT: 'recipientExternalId',
  CULPRIT: 'culpritExternalId',
  SOURCE_MATCHING_TX: 'sourceMatchingTransactionId',
  SOURCE_PAYMENT_TOKEN: 'sourcePaymentTicketToken',
  ORIGINAL_PAYOUT_TOKEN: 'originalPayoutTicketToken',
  COMPENSATION_PAYOUT_TOKEN: 'compensationPayoutTicketToken',
} as const

const SEARCH_FIELD_OPTIONS = [
  { value: SEARCH_FIELDS.RECIPIENT, label: 'Получатель (external id)' },
  { value: SEARCH_FIELDS.CULPRIT, label: 'Виновник (external id)' },
  { value: SEARCH_FIELDS.SOURCE_MATCHING_TX, label: 'matchingTransactionId' },
  { value: SEARCH_FIELDS.SOURCE_PAYMENT_TOKEN, label: 'Токен исходного депозита' },
  { value: SEARCH_FIELDS.ORIGINAL_PAYOUT_TOKEN, label: 'Токен исходной выплаты' },
  { value: SEARCH_FIELDS.COMPENSATION_PAYOUT_TOKEN, label: 'Токен компенсационной выплаты' },
]

const STATUS_OPTIONS: Array<{ value: UiStatus; label: string }> = [
  { value: 'IN_PROGRESS', label: UI_STATUS_LABEL.IN_PROGRESS },
  { value: 'COMPLETED', label: UI_STATUS_LABEL.COMPLETED },
  { value: 'DECLINED', label: UI_STATUS_LABEL.DECLINED },
]

interface InternalFilterState extends Omit<IBaseFilters, 'dateRange'> {
  reasonId?: string
  uiStatus?: string
  recipientMerchantTokens?: string[]
  culpritMerchantTokens?: string[]
  searchAdvanced?: { field: string; query: string }
  dateRange?: { from?: Date; to?: Date }
}

const DEFAULT_INTERNAL_FILTERS: InternalFilterState = {
  reasonId: '',
  uiStatus: '',
  recipientMerchantTokens: [],
  culpritMerchantTokens: [],
  searchAdvanced: { field: SEARCH_FIELDS.RECIPIENT, query: '' },
  dateRange: {},
}

export const OverpaymentsPage: React.FC = () => {
  const { currentUser } = useAuth()
  const canPayViaLocalAdapter = useMemo(
    () => IsGranted(matchingCapabilities.canPayOverpaymentViaLocalAdapter, currentUser),
    [currentUser]
  )
  const [localAdapterFor, setLocalAdapterFor] = useState<number | null>(null)
  const [items, setItems] = useState<OverpaymentRecord[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [reasons, setReasons] = useState<OverpaymentReasonLookupItem[]>([])
  const [merchantOptions, setMerchantOptions] = useState<Array<{ value: string; label: string }>>([])
  const [appliedQuery, setAppliedQuery] = useState<OverpaymentFilters>({})

  useEffect(() => {
    getOverpaymentReasonLookup()
      .then((r) => {
        if (r.data.success) setReasons(r.data.data ?? [])
      })
      .catch(() => {})

    // lookup-with-token сам скоуплен по мерчантам пользователя, поэтому в фильтре роль с портфелем
    // увидит ровно тех мерчантов, чьи записи ей отдаёт и сам журнал.
    getMerchantLookupWithToken()
      .then((r) => {
        if (r.data?.success) setMerchantOptions(r.data.data ?? [])
      })
      .catch(() => setMerchantOptions([]))
  }, [])

  const reasonOptions = useMemo(
    () => reasons.map((r) => ({ value: String(r.value), label: r.label })),
    [reasons]
  )

  const load = useCallback(async (pg: number, query: OverpaymentFilters) => {
    setLoading(true)
    try {
      const params: OverpaymentQuery = { ...query, page: pg + 1, perPage: PAGE_SIZE }
      const r = await getOverpaymentList(params)
      if (r.data.success) {
        setItems(r.data.data ?? [])
        setTotalRows(r.data.totalRows ?? 0)
      } else {
        setItems([])
        setTotalRows(0)
      }
    } catch {
      setItems([])
      setTotalRows(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(0, {})
  }, [load])

  const handleApplyFilters = (filters: InternalFilterState) => {
    const query: OverpaymentFilters = {}

    if (filters.reasonId) query.reasonId = Number(filters.reasonId)
    if (filters.uiStatus) query.status = filters.uiStatus as UiStatus

    // Токены мерчантов уходят строками — приводить нечего. Несколько значений в одном фильтре
    // бэкенд объединяет по «ИЛИ», сами фильтры между собой — по «И».
    if (filters.recipientMerchantTokens?.length) {
      query.recipientMerchantTokens = filters.recipientMerchantTokens
    }
    if (filters.culpritMerchantTokens?.length) {
      query.culpritMerchantTokens = filters.culpritMerchantTokens
    }

    const query$ = filters.searchAdvanced?.query?.trim()
    const field = filters.searchAdvanced?.field
    if (query$ && field) {
      if (field === SEARCH_FIELDS.SOURCE_MATCHING_TX) {
        const n = Number(query$)
        if (Number.isInteger(n) && n > 0) {
          query.sourceMatchingTransactionId = n
        }
      } else {
        (query as Record<string, string>)[field] = query$
      }
    }

    if (filters.dateRange?.from) query.createdAtStart = convertToUTCByMSK(filters.dateRange.from)
    if (filters.dateRange?.to) query.createdAtEnd = convertToUTCByMSK(filters.dateRange.to)

    setAppliedQuery(query)
    setPage(0)
    load(0, query)
  }

  const handlePageChange = (selected: number) => {
    setPage(selected)
    load(selected, appliedQuery)
  }

  const pageCount = Math.ceil(totalRows / PAGE_SIZE)

  return (
    <>
      <PageTitle breadcrumbs={OVERPAYMENTS_BREADCRUMBS as any}>Журнал переоплат</PageTitle>
      <FilterManager<InternalFilterState>
        activeFilters={[
          'dateRange',
          'searchAdvanced',
          { filter: 'selectMulti', fieldName: 'recipientMerchantTokens' },
          { filter: 'selectMulti', fieldName: 'culpritMerchantTokens' },
          { filter: 'select', fieldName: 'reasonId' },
          { filter: 'select', fieldName: 'uiStatus' },
        ]}
        defaultValues={DEFAULT_INTERNAL_FILTERS}
        customFilters={{
          dateRange: { component: DateRangeFilter as any, defaultValue: {} as any },
        }}
        filterProps={{
          recipientMerchantTokens: {
            options: merchantOptions,
            label: 'Мерчант-получатель',
            placeholder: 'Все',
          },
          culpritMerchantTokens: {
            options: merchantOptions,
            label: 'Мерчант-виновник',
            placeholder: 'Все',
          },
          reasonId: { options: reasonOptions, label: 'Причина', placeholder: 'Все' },
          uiStatus: { options: STATUS_OPTIONS, label: 'Статус', placeholder: 'Все' },
          searchAdvanced: { options: SEARCH_FIELD_OPTIONS, placeholder: 'Поиск' },
          dateRange: { hideRanges: true, className: 'w-100' },
        }}
        onApplyFilters={handleApplyFilters}
        actions={<ExportButton query={appliedQuery} />}
      />
      <div className='card'>
        <div className='card-body'>
          <OverpaymentsTable
            items={items}
            loading={loading}
            onPayViaLocalAdapter={
              canPayViaLocalAdapter ? (o) => setLocalAdapterFor(o.id) : undefined
            }
          />
          <TablePagination
            currentPage={page}
            pageCount={pageCount}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
      <LocalAdapterModal
        show={localAdapterFor !== null}
        overpaymentId={localAdapterFor}
        onClose={() => setLocalAdapterFor(null)}
        onDone={() => load(page, appliedQuery)}
      />
    </>
  )
}

export default OverpaymentsPage
